// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGraduationCap, faBookOpen, faChevronDown, faUserCircle, faEllipsis, faDumbbell, faGlobe, faUserShield, faKeyboard, faUser } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { moreMenuGroups } from "../../consts/navGroups";
import Logo from "../Logo/Logo";
import AchievementUnlockedNotice from "../Achievements/AchievementUnlockedNotice";
import LevelBadge from "./LevelBadge";
import JlptLevelSelector from "./JlptLevelSelector";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";
import LanguagePromptBanner from "./LanguagePromptBanner";
import OfflineBanner from "./OfflineBanner";
import GlobalSearch from "../GlobalSearch/GlobalSearch";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { isDesktopLocalProfile } from "../../utils/desktopProfile";
import styles from "./header.module.scss";

// The "More" dropdown's and mobile overflow panel's links, grouped so a
// learner can scan for the kind of thing they want instead of 20+ items in
// one flat list. Shared with the footer - see src/consts/navGroups.ts for
// the single source of truth and the reasoning behind it.
const moreLinks = moreMenuGroups.flatMap((group) => group.links);

// The mobile "Show more" panel folds in Vocabulary too, since it doesn't
// fit as a persistent pill once the nav stops scrolling horizontally. Below
// it, the same grouped data drives a flat list of rows - a heading row
// (spanning both grid columns) followed by that group's links - rather than
// a second, separately-maintained link list.
const mobileOverflowRows = [
    { type: "heading", headingKey: "header.mainNavigation" },
    { type: "link", to: "/learn", icon: faGraduationCap, labelKey: "header.nav.learn" },
    { type: "link", to: "/keyboard", icon: faKeyboard, labelKey: "header.nav.keyboard" },
    { type: "link", to: "/translate", icon: faGlobe, labelKey: "header.nav.translate" },
    { type: "link", to: "/vocabulary", icon: faBookOpen, labelKey: "header.nav.vocabulary" },
    ...moreMenuGroups.flatMap((group) => [
        { type: "heading", headingKey: group.headingKey },
        ...group.links.map((link) => ({ type: "link", ...link })),
    ]),
];

// Same breakpoint as the .nav layout switch in header.module.scss - below
// it, Vocabulary/Grammar/etc. fold into the collapsible "Show more" panel
// instead of rendering inline (desktop) or requiring a floating dropdown.
const MOBILE_NAV_QUERY = "(max-width: 1150px)";

// Within the compact layout, from this width the four primary tabs move up
// into a single 64px row next to the brand instead of sitting in their own
// full-width row underneath it (see the .mobileRow1/.mobileTabs rules).
const SINGLE_ROW_QUERY = "(min-width: 900px)";

function useCloseOnOutsideEvent(isOpen, onClose, triggerRef, extraRef) {
    const ref = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        function isInside(target) {
            return Boolean(
                ref.current?.contains(target) ||
                extraRef?.current?.contains(target) ||
                triggerRef.current?.contains(target)
            );
        }

        // Pointer-ish events (mouse and touch) only close when they land
        // outside the menu, its trigger and any extra region; Escape always
        // closes.
        function handleOutsideEvent(event) {
            if (event.type === "keydown") {
                if (event.key !== "Escape") return;
            } else if (isInside(event.target)) {
                return;
            }
            onClose();
            // Escape closes the menu out from under a keyboard user's focus -
            // send it back to the trigger button rather than dropping to <body>.
            if (event.type === "keydown") triggerRef.current?.focus();
        }

        document.addEventListener("mousedown", handleOutsideEvent);
        document.addEventListener("touchstart", handleOutsideEvent, { passive: true });
        document.addEventListener("keydown", handleOutsideEvent);
        return () => {
            document.removeEventListener("mousedown", handleOutsideEvent);
            document.removeEventListener("touchstart", handleOutsideEvent);
            document.removeEventListener("keydown", handleOutsideEvent);
        };
    }, [isOpen, onClose, triggerRef, extraRef]);

    return ref;
}

const MoreMenu = () => {
    const lp = useLocalizedPath();
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState(null);
    const location = useLocation();
    // Excludes /practice: that page already has its own primary-nav pill
    // (PracticeNavLink), so it shouldn't also light up "More" when visited.
    const isActive = moreLinks.some((link) => link.to !== "/practice" && location.pathname.startsWith(link.to));
    const triggerRef = useRef(null);
    const dropdownRef = useRef(null);
    const menuRef = useCloseOnOutsideEvent(isOpen, () => setIsOpen(false), triggerRef, dropdownRef);

    // On mobile the nav is a horizontally-scrolling strip, so this dropdown
    // is portaled to <body> and positioned in fixed coordinates - anchoring
    // it normally (absolute, relative to the trigger) would put it inside
    // the strip's scroll box, which clips anything below the strip's height.
    function handleToggle() {
        setIsOpen((open) => {
            const next = !open;
            if (next && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                // Anchor from the trigger's right edge rather than its left:
                // "More" is the last nav item, so the trigger sits near the
                // right edge of the screen on mobile and a left-anchored
                // dropdown would run off the viewport.
                const right = Math.max(8, window.innerWidth - rect.right);
                setMenuPosition({ top: rect.bottom + 8, right });
            }
            return next;
        });
    }

    // The trigger's position can change out from under an already-computed
    // fixed position - page scroll, the nav strip's own horizontal scroll,
    // or a viewport resize - so just close rather than track it live.
    useEffect(() => {
        if (!isOpen) return undefined;

        function handleReposition() {
            setIsOpen(false);
        }

        window.addEventListener("scroll", handleReposition, { passive: true, capture: true });
        window.addEventListener("resize", handleReposition);
        return () => {
            window.removeEventListener("scroll", handleReposition, { capture: true });
            window.removeEventListener("resize", handleReposition);
        };
    }, [isOpen]);

    return (
        <div className={styles.moreMenu} ref={menuRef}>
            <button
                type="button"
                ref={triggerRef}
                className={isActive ? `${styles.navLink} ${styles.active}` : styles.navLink}
                onClick={handleToggle}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-current={isActive ? "true" : undefined}
            >
                <FontAwesomeIcon icon={faEllipsis} className={styles.navIcon} />
                {t("header.nav.more")}
                <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
            </button>
            {isOpen && menuPosition &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={styles.moreMenuDropdown}
                        style={{ position: "fixed", top: menuPosition.top, right: menuPosition.right }}
                        role="menu"
                    >
                        {moreMenuGroups.map((group, index) => {
                            const headingId = `more-menu-group-${index}`;
                            return (
                                <div key={group.headingKey} className={styles.moreMenuGroup} role="group" aria-labelledby={headingId}>
                                    <p id={headingId} className={styles.moreMenuGroupHeading}>{t(group.headingKey)}</p>
                                    {group.links.map((link) => (
                                        <NavLink
                                            key={link.to}
                                            to={lp(link.to)}
                                            className={({ isActive: linkActive }) =>
                                                linkActive ? `${styles.userMenuItem} ${styles.userMenuItemActive}` : styles.userMenuItem
                                            }
                                            onClick={() => setIsOpen(false)}
                                            role="menuitem"
                                        >
                                            {link.icon && <FontAwesomeIcon icon={link.icon} className={styles.navIcon} />}
                                            {t(link.labelKey)}
                                        </NavLink>
                                    ))}
                                </div>
                            );
                        })}
                    </div>,
                    document.body
                )}
        </div>
    );
};

// `variant="avatar"` is the compact-layout treatment: a round icon button
// whose accessible name is still the display name/email (sr-only text), with
// the full name shown as a non-interactive first line of the dropdown.
const UserMenu = ({ variant = "pill" }) => {
    const lp = useLocalizedPath();
    const { t } = useTranslation();
    const { user, signOut } = useAuth();
    // Offline desktop build: a local profile has no name, email or sign-out (see utils/desktopProfile.ts).
    const displayName = isDesktopLocalProfile ? t("desktopLocalProfile.name") : user?.displayName || user?.email;
    const isAvatar = variant === "avatar";
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const triggerRef = useRef(null);
    const menuRef = useCloseOnOutsideEvent(isOpen, () => setIsOpen(false), triggerRef);

    async function handleSignOut() {
        setIsOpen(false);
        await signOut();
        navigate(lp("/"));
    }

    const menuItems = (
        <>
            <NavLink to={lp("/profile")} className={styles.userMenuItem} onClick={() => setIsOpen(false)} role="menuitem">
                {t("header.auth.profile")}
            </NavLink>
            {!isDesktopLocalProfile && (
                <button type="button" className={styles.userMenuItem} onClick={handleSignOut} role="menuitem">
                    {t("header.auth.signOut")}
                </button>
            )}
        </>
    );

    if (isAvatar) {
        return (
            <div className={styles.userMenuStatic} ref={menuRef}>
                <button
                    type="button"
                    ref={triggerRef}
                    className={styles.roundButton}
                    onClick={() => setIsOpen((open) => !open)}
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                >
                    <span className={`${styles.roundIcon} ${styles.avatarCircle}`}>
                        <FontAwesomeIcon icon={faUser} />
                    </span>
                    <span className={styles.srOnly}>{displayName}</span>
                </button>
                {isOpen && (
                    <div className={styles.avatarDropdown}>
                        <div className={styles.userMenuName} title={displayName}>
                            {displayName}
                        </div>
                        <div role="menu" className={styles.userMenuItems}>
                            {menuItems}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className={styles.userMenu} ref={menuRef}>
            <button
                type="button"
                ref={triggerRef}
                className={styles.userMenuButton}
                onClick={() => setIsOpen((open) => !open)}
                aria-haspopup="menu"
                aria-expanded={isOpen}
            >
                <FontAwesomeIcon icon={faUserCircle} className={styles.navIcon} />
                {displayName}
                <FontAwesomeIcon icon={faChevronDown} className={styles.chevron} />
            </button>
            {isOpen && (
                <div className={styles.userMenuDropdown} role="menu">
                    {menuItems}
                </div>
            )}
        </div>
    );
};

// A real /practice hub page (see PracticePage.tsx) rather than a homepage
// anchor - plain route-based active matching, same as every other nav item.
const PracticeNavLink = ({ variant = "pill" }) => {
    const lp = useLocalizedPath();
    const { t } = useTranslation();
    const isTab = variant === "tab";
    const baseClass = isTab ? styles.tab : styles.navLink;
    const activeClass = isTab ? styles.tabActive : styles.active;
    const label = t("header.nav.practice");

    return (
        <NavLink
            to={lp("/practice")}
            className={({ isActive }) => (isActive ? `${baseClass} ${activeClass}` : baseClass)}
        >
            <FontAwesomeIcon icon={faDumbbell} className={isTab ? styles.tabIcon : styles.navIcon} />
            {isTab ? <span className={styles.tabLabel}>{label}</span> : label}
        </NavLink>
    );
};

// The Learn hub itself, and the Lessons section it leads to (English and /pt).
const LEARN_HUB_PATH = /^(?:\/pt)?\/learn\/?$/;
const LESSONS_PATH = /^(?:\/pt)?\/lessons(?:\/|$)/;

// The Learn tab opens the /learn hub but stays lit while the learner is on the
// Lessons pages it links to, so the nav still shows where they are. A plain
// NavLink can't do that (it only matches its own `to`), hence the manual state:
// aria-current="page" on the hub, "true" ("somewhere in this section") on
// Lessons - the same convention as the More trigger above.
const LearnNavLink = ({ variant = "pill" }) => {
    const lp = useLocalizedPath();
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const isTab = variant === "tab";
    const baseClass = isTab ? styles.tab : styles.navLink;
    const activeClass = isTab ? styles.tabActive : styles.active;
    const label = t("header.nav.learn");

    let ariaCurrent;
    if (LEARN_HUB_PATH.test(pathname)) ariaCurrent = "page";
    else if (LESSONS_PATH.test(pathname)) ariaCurrent = "true";

    return (
        <Link
            to={lp("/learn")}
            className={ariaCurrent ? `${baseClass} ${activeClass}` : baseClass}
            aria-current={ariaCurrent}
        >
            <FontAwesomeIcon icon={faGraduationCap} className={isTab ? styles.tabIcon : styles.navIcon} />
            {isTab ? <span className={styles.tabLabel}>{label}</span> : label}
        </Link>
    );
};

// The compact layout's primary nav: four equal-width always-visible tabs
// (a 4-column grid on phones, pills in the single-row tablet layout).
const PrimaryTab = ({ to, icon, label }) => {
    const lp = useLocalizedPath();
    return (
        <NavLink to={lp(to)} className={({ isActive }) => (isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab)}>
            <FontAwesomeIcon icon={icon} className={styles.tabIcon} />
            <span className={styles.tabLabel}>{label}</span>
        </NavLink>
    );
};

const MobileTabs = () => {
    const { t } = useTranslation();
    return (
        <nav className={styles.mobileTabs} aria-label={t("header.mainNavigation")}>
            <LearnNavLink variant="tab" />
            <PracticeNavLink variant="tab" />
            <PrimaryTab to="/keyboard" icon={faKeyboard} label={t("header.nav.keyboard")} />
            <PrimaryTab to="/translate" icon={faGlobe} label={t("header.nav.translate")} />
        </nav>
    );
};

const AdminStatsLink = ({ inOverflow = false }) => {
    const lp = useLocalizedPath();
    const { t } = useTranslation();
    const { status, user, isSigningOut } = useAuth();
    const { hostname } = window.location;
    // Both the new and old admin hostnames are checked during the domain
    // migration - see aws-config.ts for why the old one is still listed.
    if ((hostname !== "admin.nihongotech.teqseq.com" && hostname !== "admin.kanalearn.teqseq.com") ||
        status !== "authenticated" || !user?.isAdmin || isSigningOut) return null;

    return (
        <NavLink
            to={lp("/stats")}
            aria-label={t("header.nav.adminStats")}
            title={t("header.nav.adminStatsTitle")}
            className={({ isActive }) => {
                if (inOverflow) {
                    return isActive ? `${styles.userMenuItem} ${styles.userMenuItemActive}` : styles.userMenuItem;
                }
                return isActive ? `${styles.navLink} ${styles.active}` : styles.navLink;
            }}
        >
            <FontAwesomeIcon icon={faUserShield} className={styles.navIcon} aria-hidden="true" />
            {t("header.nav.adminStats")}
        </NavLink>
    );
};

// At <=350px the row-1 "Log in" link is hidden by CSS and this copy in the
// overflow panel takes over. It only renders while the panel is open, so the
// page never has two "Log in" links at once outside that state.
const OverflowLoginItem = ({ onNavigate }) => {
    const lp = useLocalizedPath();
    const { t } = useTranslation();
    const { status, isSigningOut } = useAuth();
    if (status === "loading" || status === "authenticated" || isSigningOut) return null;

    return (
        <NavLink to={lp("/login")} className={`${styles.userMenuItem} ${styles.panelLogin}`} onClick={onNavigate}>
            {t("header.auth.logIn")}
        </NavLink>
    );
};

// `compact` is the phone/tablet app-bar composition: no wrapper (the caller's
// action row lays these out), a reserved 44x44 loading slot, an avatar button
// instead of the name pill, and a plain-text Log in next to a small Sign up.
const AuthArea = ({ compact = false, showStudyControls = true }) => {
    const lp = useLocalizedPath();
    const { t } = useTranslation();
    const { status, isSigningOut } = useAuth();

    if (status === "loading" || isSigningOut) {
        return (
            <div className={compact ? `${styles.authLoading} ${styles.authLoadingCompact}` : styles.authLoading} role="status">
                <span className={styles.authSpinner} aria-hidden="true" />
                <span className={styles.srOnly}>
                    {isSigningOut ? t("header.auth.signingOut") : t("header.auth.loadingAccount")}
                </span>
            </div>
        );
    }

    if (status === "authenticated" && compact) {
        return <>{showStudyControls && <><LevelBadge /><JlptLevelSelector /></>}<UserMenu variant="avatar" /></>;
    }

    if (status === "authenticated") {
        return <div className={styles.authLinks}><LevelBadge /><JlptLevelSelector /><UserMenu /></div>;
    }

    if (compact) {
        return (
            <>
                <NavLink to={lp("/login")} className={styles.loginLink}>
                    {t("header.auth.logIn")}
                </NavLink>
                <NavLink to={lp("/register")} className={styles.signUpCompact}>
                    {t("header.auth.signUp")}
                </NavLink>
            </>
        );
    }

    return (
        <div className={styles.authLinks}>
            <NavLink to={lp("/login")} className={styles.navLink}>
                {t("header.auth.logIn")}
            </NavLink>
            <NavLink to={lp("/register")} className={styles.signUpLink}>
                {t("header.auth.signUp")}
            </NavLink>
        </div>
    );
};

// Every page renders exactly one <main>, so the skip link can jump straight
// to it without needing a matching id added to every page component.
function handleSkipToContent(event) {
    event.preventDefault();
    const main = document.querySelector("main");
    if (!main) return;
    if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
    main.focus();
}

// Hides the header on downward scroll and brings it back on upward scroll (or
// near the top of the page), so it doesn't permanently occupy a big chunk of
// a small screen. Scrolls under a `revealAt` threshold near the top never
// hide it, and small jitters below `deltaThreshold` are ignored so the header
// doesn't flicker on trackpad/momentum scrolling.
function useHideOnScroll() {
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        let lastY = window.scrollY;
        let ticking = false;
        const revealAt = 96;
        const deltaThreshold = 8;

        function handleScroll() {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                const currentY = window.scrollY;
                const delta = currentY - lastY;

                if (currentY <= revealAt) {
                    setHidden(false);
                } else if (Math.abs(delta) > deltaThreshold) {
                    setHidden(delta > 0);
                }

                lastY = currentY;
                ticking = false;
            });
        }

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return [hidden, setHidden];
}

const Header = () => {
    const lp = useLocalizedPath();
    const { t } = useTranslation();
    const wrapperRef = useRef(null);
    const location = useLocation();
    const [hidden, setHidden] = useHideOnScroll();
    const isMobileNav = useMediaQuery(MOBILE_NAV_QUERY);
    const isSingleRow = useMediaQuery(SINGLE_ROW_QUERY);
    const [isOverflowOpen, setIsOverflowOpen] = useState(false);
    const overflowTriggerRef = useRef(null);
    const overflowScrimRef = useRef(null);
    const closeOverflow = useCallback(() => setIsOverflowOpen(false), []);
    const isOverflowPanelOpen = isMobileNav && isOverflowOpen;
    // Outside mouse/touch or Escape closes the panel (Escape also returns focus
    // to the trigger). The scrim is excluded here and closes on its own click,
    // so a tap on it can't unmount it before the click lands on the page below.
    const overflowPanelRef = useCloseOnOutsideEvent(isOverflowPanelOpen, closeOverflow, overflowTriggerRef, overflowScrimRef);

    useEffect(() => {
        const node = wrapperRef.current;
        if (!node || typeof ResizeObserver === "undefined") return undefined;

        const setHeaderHeightVar = () => {
            document.documentElement.style.setProperty("--header-height", `${node.offsetHeight}px`);
        };

        setHeaderHeightVar();
        const observer = new ResizeObserver(setHeaderHeightVar);
        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    // Land on every new page with the header visible rather than stuck hidden
    // from scroll position carried over off the previous page.
    useEffect(() => {
        setHidden(false);
    }, [location.pathname, setHidden]);

    // Following a link inside the panel should close it rather than leaving
    // it expanded over the next page.
    useEffect(() => {
        setIsOverflowOpen(false);
    }, [location.pathname]);

    const brand = (
        <NavLink to={lp("/")} className={styles.brandLink} aria-label={t("header.homeLabel")}>
            <Logo compact={isMobileNav} />
        </NavLink>
    );

    let content;
    if (isMobileNav) {
        const tabs = <MobileTabs />;
        content = (
            <div className={styles.mobileBar}>
                <div className={styles.mobileRow1}>
                    {brand}
                    {isSingleRow && tabs}
                    <div className={styles.mobileActions}>
                        {isSingleRow && <GlobalSearch compact />}
                        {isSingleRow && <LanguageSwitcher variant="compact" />}
                        <AuthArea compact showStudyControls={isSingleRow} />
                        <button
                            type="button"
                            ref={overflowTriggerRef}
                            className={styles.roundButton}
                            onClick={() => setIsOverflowOpen((open) => !open)}
                            aria-expanded={isOverflowOpen}
                            aria-controls="mobile-nav-overflow"
                        >
                            <span className={styles.roundIcon}>
                                <FontAwesomeIcon icon={faEllipsis} />
                            </span>
                            <span className={styles.srOnly}>
                                {isOverflowOpen ? t("header.nav.showLess") : t("header.nav.showMore")}
                            </span>
                        </button>
                    </div>
                </div>
                {!isSingleRow && (
                    <div className={styles.mobileSearchRow}>
                        <GlobalSearch />
                    </div>
                )}
                {isOverflowOpen && (
                    <>
                        <div
                            ref={overflowScrimRef}
                            className={styles.overflowScrim}
                            aria-hidden="true"
                            onClick={closeOverflow}
                        />
                        <div id="mobile-nav-overflow" ref={overflowPanelRef} className={styles.navOverflowPanel}>
                            <OverflowLoginItem onNavigate={closeOverflow} />
                            <AdminStatsLink inOverflow />
                            {!isSingleRow && (
                                <div className={styles.mobileSecondaryControls}>
                                    <LevelBadge />
                                    <JlptLevelSelector />
                                    <LanguageSwitcher variant="compact" />
                                </div>
                            )}
                            {mobileOverflowRows.map((row) => {
                                if (row.type === "heading") {
                                    return (
                                        <p key={row.headingKey} className={styles.navOverflowGroupHeading}>
                                            {t(row.headingKey)}
                                        </p>
                                    );
                                }
                                return (
                                    <NavLink
                                        key={row.to}
                                        to={lp(row.to)}
                                        className={({ isActive }) =>
                                            isActive ? `${styles.userMenuItem} ${styles.userMenuItemActive}` : styles.userMenuItem
                                        }
                                        onClick={closeOverflow}
                                    >
                                        {row.icon && <FontAwesomeIcon icon={row.icon} className={styles.navIcon} />}
                                        {t(row.labelKey)}
                                    </NavLink>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        );
    } else {
        content = (
            <div className={styles.header}>
                {brand}

                <nav className={styles.nav} aria-label={t("header.mainNavigation")}>
                    <LearnNavLink />
                    <PracticeNavLink />
                    <NavLink
                        to={lp("/keyboard")}
                        className={({ isActive }) =>
                            isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                        }
                    >
                        <FontAwesomeIcon icon={faKeyboard} className={styles.navIcon} />
                        {t("header.nav.keyboard")}
                    </NavLink>
                    <NavLink
                        to={lp("/translate")}
                        className={({ isActive }) =>
                            isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                        }
                    >
                        <FontAwesomeIcon icon={faGlobe} className={styles.navIcon} />
                        {t("header.nav.translate")}
                    </NavLink>
                    <NavLink
                        to={lp("/vocabulary")}
                        className={({ isActive }) =>
                            isActive ? `${styles.navLink} ${styles.active}` : styles.navLink
                        }
                    >
                        <FontAwesomeIcon icon={faBookOpen} className={styles.navIcon} />
                        {t("header.nav.vocabulary")}
                    </NavLink>
                    <AdminStatsLink />
                    <MoreMenu />
                </nav>

                <div className={styles.headerActions}>
                    <GlobalSearch />
                    <LanguageSwitcher />
                    <ThemeSwitcher />
                    <AuthArea />
                </div>
            </div>
        );
    }

    // While the overflow panel is open the header stays put, even mid-scroll
    // - hiding it would strand the panel (and its trigger) off-screen.
    const isHidden = hidden && !isOverflowPanelOpen;

    return (
        <>
            <AchievementUnlockedNotice />
            <a href="#main-content" className={styles.skipLink} onClick={handleSkipToContent}>
                {t("header.skipToContent")}
            </a>
            <header
                className={isHidden ? `${styles.wrapper} ${styles.hidden}` : styles.wrapper}
                ref={wrapperRef}
            >
                {content}
                <OfflineBanner />
                <LanguagePromptBanner />
            </header>
        </>
    );
};

export default Header;
